/**
 * TwoFactorSetup Wizard Component
 *
 * Multi-step wizard for setting up TOTP-based two-factor authentication.
 *
 * Steps:
 * 1. Introduction - Explain 2FA and its benefits
 * 2. Setup - Display QR code and secret key
 * 3. Verify - Input and verify TOTP code
 * 4. Backup Codes - Display and save backup codes
 * 5. Complete - Success confirmation
 *
 * Features:
 * - Step indicators with progress tracking
 * - QR code display for authenticator app setup
 * - Copy-to-clipboard for secret key and backup codes
 * - Download backup codes as text file
 * - Comprehensive error handling
 * - Loading states during API calls
 * - Keyboard shortcuts (Enter to proceed, Escape to close)
 * - WCAG 2.1 AA accessibility compliance
 */

import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useProfileStore } from '@/stores/profile/profile-store';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Shield,
  Smartphone,
  CheckCircle2,
  Copy,
  Download,
  AlertCircle,
  Loader2,
  Key,
  ChevronRight,
  ChevronLeft,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

interface TwoFactorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onEnabled?: () => void;
  onComplete?: () => void;
}

type WizardStep = 'introduction' | 'setup' | 'verify' | 'backup' | 'complete';

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  isOpen,
  onClose,
  onEnabled,
  onComplete,
}) => {
  // Store actions
  const {
    setup2FA,
    enable2FA,
    generateBackupCodes,
    twoFactorError,
    clear2FAError,
  } = useProfileStore();

  // Step management
  const [currentStep, setCurrentStep] = useState<WizardStep>('introduction');

  // Setup data
  const [secret, setSecret] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupCodesError, setBackupCodesError] = useState<string>('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Verification
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [verificationError, setVerificationError] = useState<string>('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Refs for focus management
  const verificationInputRef = useRef<HTMLInputElement>(null);

  // Steps configuration
  const steps: WizardStep[] = [
    'introduction',
    'setup',
    'verify',
    'backup',
    'complete',
  ];
  const stepIndex = steps.indexOf(currentStep);
  const progressPercentage = ((stepIndex + 1) / steps.length) * 100;

  // Reset wizard when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('introduction');
      setSecret('');
      setQrCode('');
      setBackupCodes([]);
      setBackupCodesError('');
      setTwoFactorEnabled(false);
      setVerificationCode('');
      setVerificationError('');
      setIsSubmitting(false);
      setCopiedSecret(false);
      setCopiedBackupCodes(false);
      clear2FAError();
    }
  }, [isOpen, clear2FAError]);

  // Focus verification input when on verify step
  useEffect(() => {
    if (currentStep === 'verify' && verificationInputRef.current) {
      setTimeout(() => {
        verificationInputRef.current?.focus();
      }, 100);
    }
  }, [currentStep]);

  // Handle setup (Step 2)
  const handleSetup = async () => {
    setIsSubmitting(true);
    try {
      const setupData = await setup2FA();
      setSecret(setupData.secret);
      setQrCode(setupData.qrCode);
      setCurrentStep('setup');
      toast.success('2FA setup initiated', {
        description: 'Scan the QR code with your authenticator app.',
      });
    } catch (error) {
      console.error('Failed to setup 2FA:', error);
      toast.error('Failed to setup 2FA', {
        description:
          error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate backup codes after 2FA is enabled
  const handleGenerateBackupCodes = async () => {
    setIsSubmitting(true);
    setBackupCodesError('');

    try {
      const codes = await generateBackupCodes();
      setBackupCodes(codes);
      toast.success('Backup codes generated', {
        description: 'Save your backup codes to complete setup.',
      });
    } catch (error) {
      console.error('Failed to generate backup codes:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate backup codes';
      setBackupCodesError(errorMessage);
      toast.error('Failed to generate backup codes', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle verification (Step 3)
  const handleVerify = async () => {
    // Validate code format (6 digits)
    if (!/^\d{6}$/.test(verificationCode)) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    setIsSubmitting(true);
    setVerificationError('');
    setBackupCodesError('');

    try {
      await enable2FA(verificationCode);
      setTwoFactorEnabled(true);
    } catch (error) {
      console.error('Failed to verify 2FA:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Invalid verification code';
      setVerificationError(errorMessage);
      toast.error('Verification failed', {
        description: errorMessage,
      });
      setIsSubmitting(false);
      return;
    }

    setCurrentStep('backup');
    toast.success('2FA enabled successfully', {
      description: 'Generating backup codes now.',
    });

    try {
      const codes = await generateBackupCodes();
      setBackupCodes(codes);
      toast.success('Backup codes generated', {
        description: 'Save your backup codes to complete setup.',
      });
    } catch (error) {
      console.error('Failed to generate backup codes:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate backup codes';
      setBackupCodesError(errorMessage);
      toast.error('Failed to generate backup codes', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy secret key to clipboard
  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      toast.success('Secret key copied', {
        description: 'Paste it into your authenticator app.',
      });
      setTimeout(() => setCopiedSecret(false), 3000);
    } catch (_error) {
      toast.error('Failed to copy', {
        description: 'Please try selecting and copying manually.',
      });
    }
  };

  // Copy backup codes to clipboard
  const handleCopyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopiedBackupCodes(true);
      toast.success('Backup codes copied', {
        description: 'Save them in a secure location.',
      });
      setTimeout(() => setCopiedBackupCodes(false), 3000);
    } catch (_error) {
      toast.error('Failed to copy', {
        description: 'Please try selecting and copying manually.',
      });
    }
  };

  // Download backup codes as text file
  const handleDownloadBackupCodes = () => {
    try {
      const content = `TerraMatrix Two-Factor Authentication Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nKeep these codes in a secure location. Each code can only be used once.\n\n${backupCodes.join('\n')}\n`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `terramatrix-backup-codes-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Backup codes downloaded', {
        description: 'File saved to your downloads folder.',
      });
    } catch (_error) {
      toast.error('Failed to download', {
        description: 'Please try copying the codes manually.',
      });
    }
  };

  // Navigate steps
  const goToNextStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const goToPreviousStep = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  // Handle wizard completion
  const handleComplete = () => {
    toast.success('Two-Factor Authentication Enabled', {
      description: 'Your account is now more secure.',
      duration: 5000,
    });
    onComplete?.();
    onClose();
  };

  // Handle modal close with confirmation on certain steps
  const handleModalClose = () => {
    if (currentStep === 'backup' && isSubmitting) {
      return;
    }

    if (currentStep === 'verify') {
      const confirmClose = window.confirm(
        'Are you sure you want to close? Your 2FA setup is not complete and will be cancelled.',
      );
      if (!confirmClose) return;
    }

    if (currentStep === 'backup') {
      const confirmClose = window.confirm(
        backupCodes.length > 0
          ? 'Are you sure you want to close? Save your backup codes before leaving this setup.'
          : '2FA is enabled, but backup codes were not generated. Close setup and generate them later from security settings?',
      );
      if (!confirmClose) return;

      if (twoFactorEnabled) {
        onEnabled?.();
      }
    }
    onClose();
  };

  // Keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleModalClose();
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'introduction':
        return (
          <div className="space-y-6 py-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <Shield className="w-12 h-12 text-primary" />
              </div>
            </div>

            <div className="space-y-4 text-center">
              <h3 className="text-lg font-semibold text-foreground">
                Secure Your Account with Two-Factor Authentication
              </h3>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication (2FA) adds an extra layer of security
                to your account by requiring a verification code from your
                mobile device in addition to your password.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Enhanced Security
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Protect your account from unauthorized access
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Smartphone className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Easy to Use
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Use any authenticator app like Google Authenticator or Authy
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Key className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Backup Codes
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Get backup codes in case you lose access to your device
                  </p>
                </div>
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You'll need an authenticator app on your mobile device to
                complete this setup. Popular options include Google
                Authenticator, Microsoft Authenticator, or Authy.
              </AlertDescription>
            </Alert>
          </div>
        );

      case 'setup':
        return (
          <div className="space-y-6 py-6">
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-semibold text-foreground">
                Scan QR Code with Your Authenticator App
              </h3>
              <p className="text-sm text-muted-foreground">
                Open your authenticator app and scan this QR code to add your
                TerraMatrix account.
              </p>
            </div>

            {qrCode ? (
              <div className="space-y-4">
                {/* QR Code Display */}
                <div className="flex justify-center">
                  <div className="p-4 rounded-lg bg-white border-2 border-muted">
                    <QRCodeSVG value={qrCode} size={200} level="H" />
                  </div>
                </div>

                {/* Manual Entry Option */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Can't scan the QR code? Enter this secret key manually:
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={secret}
                      readOnly
                      className="font-mono text-sm"
                      aria-label="Secret key for manual entry"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopySecret}
                      disabled={copiedSecret}
                      aria-label={
                        copiedSecret ? 'Secret key copied' : 'Copy secret key'
                      }
                    >
                      {copiedSecret ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Alert>
                  <Smartphone className="h-4 w-4" />
                  <AlertDescription>
                    After scanning or entering the code, your authenticator app
                    will display a 6-digit verification code that changes every
                    30 seconds.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
          </div>
        );

      case 'verify':
        return (
          <div className="space-y-6 py-6">
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-semibold text-foreground">
                Enter Verification Code
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app to verify the
                setup.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  ref={verificationInputRef}
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setVerificationError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && verificationCode.length === 6) {
                      handleVerify();
                    }
                  }}
                  className={`text-center text-2xl tracking-widest font-mono ${verificationError ? 'border-red-500' : ''}`}
                  aria-invalid={!!verificationError}
                  aria-describedby={
                    verificationError
                      ? 'verification-error'
                      : 'verification-hint'
                  }
                  disabled={isSubmitting}
                />
                {verificationError && (
                  <div
                    id="verification-error"
                    className="flex items-center gap-2 text-sm text-red-500"
                    role="alert"
                  >
                    <XCircle className="w-4 h-4" />
                    {verificationError}
                  </div>
                )}
                {!verificationError && (
                  <p
                    id="verification-hint"
                    className="text-xs text-muted-foreground"
                  >
                    The code refreshes every 30 seconds
                  </p>
                )}
              </div>

              {twoFactorError && (
                <Alert>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <AlertDescription className="text-red-500">
                    {twoFactorError}
                  </AlertDescription>
                </Alert>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Make sure the time on your device is synchronized. Time-based
                  codes require accurate device time to work properly.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );

      case 'backup':
        return (
          <div className="space-y-6 py-6">
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-semibold text-foreground">
                Save Backup Codes
              </h3>
              <p className="text-sm text-muted-foreground">
                Store these backup codes in a safe place. You can use them to
                access your account if you lose your device.
              </p>
            </div>

            <div className="space-y-4">
              {/* Backup Codes Display */}
              {backupCodes.length > 0 ? (
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className="font-mono text-sm text-center p-2 rounded bg-background border"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border bg-muted/50 p-6 text-center">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Generating backup codes...
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please keep this setup open while codes are generated.
                      </p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-6 h-6 text-red-500" />
                      <p className="text-sm text-muted-foreground">
                        Backup codes could not be generated. Your 2FA is
                        enabled, and you can retry code generation now.
                      </p>
                    </>
                  )}
                </div>
              )}

              {backupCodesError && (
                <Alert>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <AlertDescription className="text-red-500">
                    {backupCodesError}
                  </AlertDescription>
                </Alert>
              )}

              {backupCodes.length === 0 && (
                <Button
                  variant="outline"
                  onClick={handleGenerateBackupCodes}
                  disabled={isSubmitting}
                  className="w-full"
                  aria-label="Retry backup code generation"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Retry Backup Codes
                    </>
                  )}
                </Button>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCopyBackupCodes}
                  disabled={backupCodes.length === 0 || copiedBackupCodes}
                  className="flex-1"
                  aria-label={
                    copiedBackupCodes
                      ? 'Backup codes copied'
                      : 'Copy backup codes'
                  }
                >
                  {copiedBackupCodes ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadBackupCodes}
                  disabled={backupCodes.length === 0}
                  className="flex-1"
                  aria-label="Download backup codes as text file"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Important:</strong> Each backup code can only be used
                  once. After using all codes, you can generate new ones from
                  your profile settings.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6 py-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/10 p-4">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
            </div>

            <div className="space-y-4 text-center">
              <h3 className="text-lg font-semibold text-foreground">
                Two-Factor Authentication Enabled!
              </h3>
              <p className="text-sm text-muted-foreground">
                Your account is now protected with two-factor authentication.
                You'll need to enter a verification code from your authenticator
                app each time you sign in.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Setup Complete
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your authenticator app is configured
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Backup Codes Saved
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Keep them safe for emergency access
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Account Secured
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your account now has enhanced protection
                  </p>
                </div>
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You can manage your 2FA settings, view backup codes, or disable
                2FA anytime from your profile security settings.
              </AlertDescription>
            </Alert>
          </div>
        );

      default:
        return null;
    }
  };

  // Render footer buttons
  const renderFooterButtons = () => {
    switch (currentStep) {
      case 'introduction':
        return (
          <>
            <Button
              variant="outline"
              onClick={handleModalClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetup}
              disabled={isSubmitting}
              aria-label="Start 2FA setup"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  Get Started
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </>
        );

      case 'setup':
        return (
          <>
            <Button
              variant="outline"
              onClick={goToPreviousStep}
              disabled={isSubmitting}
              aria-label="Go back to previous step"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={goToNextStep}
              disabled={!qrCode || isSubmitting}
              aria-label="Continue to verification"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        );

      case 'verify':
        return (
          <>
            <Button
              variant="outline"
              onClick={goToPreviousStep}
              disabled={isSubmitting}
              aria-label="Go back to QR code"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleVerify}
              disabled={isSubmitting}
              aria-label="Verify code and enable 2FA"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Enable
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </>
        );

      case 'backup':
        return (
          <>
            <Button
              variant="outline"
              onClick={handleModalClose}
              disabled={isSubmitting}
              aria-label="Skip and finish later"
            >
              Finish Later
            </Button>
            <Button
              onClick={handleComplete}
              disabled={backupCodes.length === 0 || isSubmitting}
              aria-label="Complete 2FA setup"
            >
              Finish Setup
              <CheckCircle2 className="w-4 h-4 ml-2" />
            </Button>
          </>
        );

      case 'complete':
        return (
          <Button
            onClick={handleComplete}
            className="w-full"
            aria-label="Close wizard"
          >
            Done
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent
        className="sm:max-w-[600px]"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Two-Factor Authentication Setup</DialogTitle>
          <DialogDescription>
            Step {stepIndex + 1} of {steps.length}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="px-6">
          <Progress
            value={progressPercentage}
            className="h-2"
            aria-label="Setup progress"
          />
        </div>

        {/* Step Content */}
        <div className="px-6">{renderStepContent()}</div>

        {/* Footer */}
        <DialogFooter>{renderFooterButtons()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
